'use client';

import { useState, useRef, useEffect } from 'react';

interface LazyVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  webmSrc?: string;
  poster: string;
  className?: string;
  videoClassName?: string;
}

export function LazyVideo({
  src,
  webmSrc,
  poster,
  className = '',
  videoClassName = '',
  autoPlay = true,
  loop = true,
  playsInline = true,
  muted = true,
  controlsList = 'nodownload',
  ...props
}: LazyVideoProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsLoaded(true);
          if (videoRef.current && autoPlay) {
            videoRef.current.play().catch(() => {});
          }
        } else {
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      {
        rootMargin: '100px', // Load video slightly before it comes into the viewport
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [autoPlay]);

  return (
    <div ref={containerRef} className={`relative w-full h-full overflow-hidden ${className}`}>
      {/* Show poster image if the video is not loaded yet */}
      {!isLoaded && (
        <img
          src={poster}
          alt="Video placeholder"
          className={`w-full h-full object-cover transition-opacity duration-300 ${videoClassName}`}
          loading="lazy"
        />
      )}

      {/* Render the video only after it enters viewport */}
      {isLoaded && (
        <video
          ref={videoRef}
          autoPlay={autoPlay}
          loop={loop}
          playsInline={playsInline}
          muted={muted}
          preload="none"
          poster={poster}
          controlsList={controlsList}
          className={`w-full h-full ${videoClassName}`}
          onLoadedData={(e) => {
            if (autoPlay) {
              e.currentTarget.play().catch(() => {});
            }
          }}
          {...props}
        >
          {webmSrc && <source src={webmSrc} type="video/webm" />}
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
}
